import React from 'react';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import ScrollReveal from '../components/ScrollReveal';
import { Helmet } from 'react-helmet';

const PolicyTerms = () => {
  return (
    <div className='font-nixie-one bg-white min-h-screen text-gray-800'>
      <Helmet>
        <title>Privacy Policy & Terms | VRIFY Africa</title>
        <meta
          name="description"
          content="Read the privacy policy and terms of service for VRIFY Africa, including information about virtual reality, partnerships, support, booking, and Ghanaian laws."
        />
      </Helmet>

      <PageHeader />
      <Preloader />

      <main className="container mx-auto px-4 py-16">
        {/* Page Title */}
        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
          <h1 className="text-4xl md:text-5xl font-cormorant-upright font-bold text-center mb-8">
            Privacy Policy &amp; Terms of Service
          </h1>
        </ScrollReveal>

        {/* Privacy Policy Section */}
        <section className="space-y-8">
          <ScrollReveal animationClass="animate-fade-in" threshold={0.1} delay={400}>
            <h2 className="text-2xl font-semibold">Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              At VRIFY Africa we take your privacy seriously. This section explains how we
              collect, use, and protect the information you provide when using our virtual
              reality tours, booking services, or interacting with our partners.
            </p>
            <h3 className="text-xl font-semibold">Information We Collect</h3>
            <p className="text-gray-700 leading-relaxed">
              We may collect personal data such as your name, email address, phone number,
              payment details and preferences when you register, request support, or make a
              booking. Technical information like IP address, device type and browser may
              also be gathered automatically to improve your experience.
            </p>
            <h3 className="text-xl font-semibold">Use of Data</h3>
            <p className="text-gray-700 leading-relaxed">
              Data is used to fulfil your bookings, provide technical and customer support,
              personalize content, and to communicate updates or promotional offers. We will
              never sell your personal information to third parties; however we may share it
              with trusted partners who help deliver our services (e.g. payment processors,
              virtual tour hosts) under strict confidentiality agreements.
            </p>
            <h3 className="text-xl font-semibold">Partnerships &amp; Third Parties</h3>
            <p className="text-gray-700 leading-relaxed">
              When you engage with experiences created in collaboration with our partners,
              their own privacy policies may apply. We encourage you to review those
              policies before providing any additional information.
            </p>
            <h3 className="text-xl font-semibold">Technical &amp; Other Support</h3>
            <p className="text-gray-700 leading-relaxed">
              To troubleshoot issues or answer questions, you may contact our support team
              via email or phone. Any information you share will be used solely to resolve
              the issue and will not be retained longer than necessary.
            </p>
            <h3 className="text-xl font-semibold">Legal Compliance</h3>
            <p className="text-gray-700 leading-relaxed">
              We comply with the Ghana Data Protection Act 2012 (Act 843) and other
              applicable privacy laws. You have the right to access, correct or request
              deletion of your personal data by contacting us at business@vrifyafrica.com.
            </p>
          </ScrollReveal>
        </section>

        {/* Terms of Service Section */}
        <section className="mt-12 space-y-8">
          <ScrollReveal animationClass="animate-fade-in" threshold={0.1} delay={600}>
            <h2 className="text-2xl font-semibold">Terms of Service</h2>
            <p className="text-gray-700 leading-relaxed">
              By using our platform you agree to these terms. Please read them carefully as
              they govern your access to virtual tours, bookings, and other interactive
              features.
            </p>
            <h3 className="text-xl font-semibold">Eligibility &amp; Account</h3>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old or have parental consent to book or use our
              services. You are responsible for maintaining the confidentiality of your
              account credentials and for all activity arising from your account.
            </p>
            <h3 className="text-xl font-semibold">Virtual Reality Experiences</h3>
            <p className="text-gray-700 leading-relaxed">
              Our VR tours are provided as-is. We make no warranties regarding accuracy,
              availability or suitability for any purpose. Use of VR equipment and software
              is at your own risk.
            </p>
            <h3 className="text-xl font-semibold">Partnership &amp; Content</h3>
            <p className="text-gray-700 leading-relaxed">
              Content produced in partnership with third parties may be subject to additional
              terms. VRIFY Africa is not liable for actions or omissions of our partners.
            </p>
            <h3 className="text-xl font-semibold">Technical Support</h3>
            <p className="text-gray-700 leading-relaxed">
              We strive to provide timely assistance but do not guarantee uninterrupted
              service. Support may be limited by factors beyond our control.
            </p>
            <h3 className="text-xl font-semibold">Booking &amp; Cancellation</h3>
            <p className="text-gray-700 leading-relaxed">
              Booking a virtual tour constitutes a binding agreement. Fees are stated at the
              time of booking and may be subject to change. Cancellations must be made 24
              hours in advance for a full refund; otherwise, a percentage may be retained
              as indicated during checkout.
            </p>
            <h3 className="text-xl font-semibold">Intellectual Property</h3>
            <p className="text-gray-700 leading-relaxed">
              All content, trademarks, and logos are the property of VRIFY Africa or its
              licensors. Unauthorized use is prohibited.
            </p>
            <h3 className="text-xl font-semibold">Limitation of Liability</h3>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, VRIFY Africa shall not be liable for
              indirect, incidental or consequential damages arising from your use of the
              service.
            </p>
            <h3 className="text-xl font-semibold">Changes to Policy</h3>
            <p className="text-gray-700 leading-relaxed">
              We may update these policies at any time. The revised version will be posted
              on this page with an updated effective date. Continued use constitutes
              acceptance of the changes.
            </p>
          </ScrollReveal>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PolicyTerms;